import React, { useState, useEffect } from "react";
import { Input, Form, Col, Row, Button, Select, Switch, Tooltip } from "antd";
import { PlusOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import RequestHandler from "util/RequestHandler";
import { defaultValidateMessages } from "./Helper";

const formItemLayout = {
  labelCol: {
    xs: {
      span: 24,
    },
    sm: {
      span: 8,
    },
  },
  wrapperCol: {
    xs: {
      span: 24,
    },
    sm: {
      span: 16,
    },
  },
};

const FormItemLabelWithTooltip = ({ label, tooltip }) => {
  return (
    <>
      {label}{" "}
      <Tooltip title={tooltip}>
        <QuestionCircleOutlined
          className="gx-text-blue"
          style={{ marginLeft: 5, fontSize: "120%" }}
        />
      </Tooltip>
    </>
  );
};

const PosCollectionBankInfoAddForm = (props) => {
  const [loading, setLoading] = useState(false);
  const [bankSelect, setBankSelect] = useState({
    data: [],
    loading: false,
  });
  const [activeBankId, setActiveBankId] = useState();
  const [bankRequirements, setBankRequirements] = useState([]);
  const [form] = Form.useForm();

  useEffect(() => {
    getNotAddedBanks();
    getBankInfoRequirements();
  }, []);

  useEffect(() => {
    if (props.data) {
      form.setFieldsValue(props.data);
    }
  }, [props.data]);

  const getNotAddedBanks = () => {
    setBankSelect({
      ...bankSelect,
      loading: true,
    });
    RequestHandler.get(
      `/PosCollectionBankInfo/GetAllBanks?$select=id,name&$filter=id eq 836`
    ).then((response) => {
      setBankSelect({
        data: response,
        loading: false,
      });
    });
  };

  const getBankInfoRequirements = () => {
    RequestHandler.get(`/PosCollectionBankInfoRequirement`).then((response) => {
      if (Array.isArray(response.value)) {
        setBankRequirements(response.value);
      }
    });
  };

  const onFinish = (values) => {
    values.notifyMails = values.notifyMails?.join(",");
    values.cancelOrRefundNotifyMails =
      values.cancelOrRefundNotifyMails?.join(",");

    RequestHandler.post(`/PosCollectionBankInfo`, values).then((response) => {
      setLoading(false);
      if (response.success) {
        getNotAddedBanks();
        form.resetFields();
        if (props.onSuccess) props.onSuccess();
      }
    });
  };

  const onFinishFailed = () => setLoading(false);

  const onBankChange = (bankId) => {
    setActiveBankId(bankId);
  };

  const bankRequirement = bankRequirements.find(
    (x) => x.bankId === activeBankId
  );

  return (
    <Form
      {...formItemLayout}
      validateMessages={defaultValidateMessages}
      form={form}
      name="posCollectionBankInfoAdd"
      initialValues={{
        remember: true,
      }}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
    >
      <Row>
        <Col span={24}>
          <Form.Item name="name" label="Tanımlama Adı :">
            <Input />
          </Form.Item>
          <Form.Item name="bankId" label="Banka" rules={[{ required: true }]}>
            <Select
              allowClear
              showSearch
              placeholder="Seçiniz"
              onChange={onBankChange}
              filterOption={(input, option) =>
                option.children
                  .toLocaleLowerCase("tr-TR")
                  .indexOf(input.toLocaleLowerCase("tr-TR")) >= 0
              }
              loading={bankSelect.loading}
            >
              {bankSelect.data.map((item) => (
                <Select.Option key={item.id} value={item.id}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {bankRequirement && (
            <>
              {bankRequirement.isUserNameRequired && (
                <Form.Item
                  label={
                    bankRequirement.userNameLabel
                      ? bankRequirement.userNameLabel
                      : "Kullanıcı Adı"
                  }
                  name="userName"
                  rules={[{ required: true, whitespace: true }]}
                >
                  <Input />
                </Form.Item>
              )}

              {bankRequirement.isPasswordRequired && (
                <Form.Item
                  label={
                    bankRequirement.passwordLabel
                      ? bankRequirement.passwordLabel
                      : "Şifre"
                  }
                  name="password"
                  rules={[{ required: true, whitespace: true }]}
                >
                  <Input.Password autoComplete="new-password" />
                </Form.Item>
              )}

              {bankRequirement.isFirmKeyRequired && (
                <Form.Item
                  label={
                    bankRequirement.firmKeyLabel
                      ? bankRequirement.firmKeyLabel
                      : "Firma Anahtarı"
                  }
                  name="firmKey"
                  rules={[{ required: true, whitespace: true }]}
                >
                  <Input />
                </Form.Item>
              )}

              {bankRequirement.isFirmSecretRequired && (
                <Form.Item
                  label={
                    bankRequirement.firmSecretLabel
                      ? bankRequirement.firmSecretLabel
                      : "Firma Gizli Anahtarı"
                  }
                  name="firmSecret"
                  rules={[{ required: true, whitespace: true }]}
                >
                  <Input />
                </Form.Item>
              )}

              {bankRequirement.isEncKeyRequired && (
                <Form.Item
                  label={
                    bankRequirement.encKeyLabel
                      ? bankRequirement.encKeyLabel
                      : "Enc Key"
                  }
                  name="encKey"
                  rules={[{ required: true, whitespace: true }]}
                >
                  {[10, 12, 32, 46, 64, 206, 59].includes(
                    bankRequirement.bankId
                  ) ? (
                    <Select>
                      {[
                        { id: "3d", name: "3D" },
                        { id: "3d_pay", name: "3D Pay" },
                        { id: "3d_pay_hosting", name: "3D Pay Hosting" },
                        { id: "Non_3d", name: "2D" },
                      ].map((item) => (
                        <Select.Option key={item.id} value={item.id}>
                          {item.name}
                        </Select.Option>
                      ))}
                    </Select>
                  ) : (
                    <Input />
                  )}
                </Form.Item>
              )}

              {bankRequirement.isOtherVirtualPosRequired && (
                <Form.Item
                  label="Sanal Pos Sağlayıcısı"
                  name="isOtherVirtualPos"
                  valuePropName="checked"
                  help={bankRequirement.isOtherVirtualPosLabel}
                >
                  <Switch />
                </Form.Item>
              )}

              {bankRequirement.isSectionCodeRequired && (
                <Form.Item
                  label={
                    bankRequirement.sectionCodeLabel
                      ? bankRequirement.sectionCodeLabel
                      : "Bölüm Kodu"
                  }
                  name="sectionCode"
                  rules={[{ required: false, whitespace: true }]}
                >
                  <Input />
                </Form.Item>
              )}

              <Form.Item
                label="Ödeme Bildirim E-postaları"
                name="notifyMails"
                rules={[
                  {
                    validator: (rule, values) => {
                      if (!values) {
                        return Promise.resolve();
                      }

                      const emailRegex =
                        /^([a-z0-9_\.-]+)@([\da-z\.-]+)\.([a-z\.]{2,6})$/;
                      const invalidInputs = values.filter(
                        (value) => !value.match(emailRegex)
                      );
                      if (invalidInputs.length === 0) {
                        return Promise.resolve();
                      }

                      if (invalidInputs.length === 1) {
                        return Promise.reject(
                          new Error(
                            `'${invalidInputs.join(
                              ""
                            )}' geçersiz bir e-postadır.`
                          )
                        );
                      } else {
                        return Promise.reject(
                          new Error(
                            `'${invalidInputs.join(
                              ", "
                            )}' değerleri geçersiz e-postadır.`
                          )
                        );
                      }
                    },
                  },
                ]}
              >
                <Select
                  allowClear
                  showSearch
                  tokenSeparators={[",", ";"]}
                  mode="tags"
                  placeholder={
                    "Girilen mail adreslerine tahsilat sonrası bildirim maili atılır."
                  }
                ></Select>
              </Form.Item>

              <Form.Item
                label="İptal İade Bildirim E-postaları"
                name="cancelOrRefundNotifyMails"
                rules={[
                  {
                    validator: (rule, values) => {
                      if (!values) {
                        return Promise.resolve();
                      }

                      const emailRegex =
                        /^([a-z0-9_\.-]+)@([\da-z\.-]+)\.([a-z\.]{2,6})$/;
                      const invalidInputs = values.filter(
                        (value) => !value.match(emailRegex)
                      );
                      if (invalidInputs.length === 0) {
                        return Promise.resolve();
                      }

                      if (invalidInputs.length === 1) {
                        return Promise.reject(
                          new Error(
                            `'${invalidInputs.join(
                              ""
                            )}' geçersiz bir e-postadır.`
                          )
                        );
                      } else {
                        return Promise.reject(
                          new Error(
                            `'${invalidInputs.join(
                              ", "
                            )}' değerleri geçersiz e-postadır.`
                          )
                        );
                      }
                    },
                  },
                ]}
              >
                <Select
                  allowClear
                  showSearch
                  tokenSeparators={[",", ";"]}
                  mode="tags"
                  placeholder={
                    "Girilen mail adreslerine iptal, iade sonrası bildirim maili atılır."
                  }
                ></Select>
              </Form.Item>

              <Form.Item label="Erp Kod" name="erpBankFirmCode">
                <Input placeholder="Yalnızca Erp aktarım için kullanılır." />
              </Form.Item>

              {bankRequirement.isUsePreAuthRequired && (
                <Form.Item
                  label="Provizyon İşlemi"
                  name="isUsePreAuth"
                  valuePropName="checked"
                  help={bankRequirement.isUsePreAuthLabel}
                >
                  <Switch />
                </Form.Item>
              )}

              <Form.Item
                label={
                  <FormItemLabelWithTooltip
                    label="Komisyon Hesaplama Seçimi"
                    tooltip={
                      <>
                        <p>
                          <strong>Seçenek aktif edilirse:</strong>
                          <ul>
                            <li>Komisyonlar aşağıdaki gibi hesaplanır.</li>
                            <li>
                              (GirilenTutar/(100-KomisyonOranı))*100
                              =ToplamTutar
                            </li>
                            <li>Örn : (1000/(100-2.5))*100 = 1025,64</li>
                          </ul>
                        </p>
                        <p>
                          <strong>Pasif edilirse:</strong>
                          <ul>
                            <li>Komisyonlar aşağıdaki gibi hesaplanır.</li>
                            <li>
                              GirilenTutar+(GirilenTutar/100)*KomisyonOranı=ToplamTutar
                            </li>
                            <li>Örn : 1000+(1000/100)*2.5 = 1025,00</li>
                          </ul>
                        </p>
                      </>
                    }
                  />
                }
                name="isPosCollectionCommissionFromDealer"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item label="Aktif" name="isActive" valuePropName="checked">
                <Switch />
              </Form.Item>

              <Form.Item
                label="Test"
                name="isTest"
                valuePropName="checked"
                help="Aktif edilirse ödeme servisinin test ortamında çalışır."
              >
                <Switch />
              </Form.Item>

              <Form.Item
                label="Taksitli İşlem"
                name="isInstallmentRequired"
                valuePropName="checked"
                help="Aktif edilirse ödeme servisinin taksitli işlemleri kullanılabilir."
              >
                <Switch />
              </Form.Item>

              <Form.Item
                label="Tek Çekim Pos'u"
                name="isDefault"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </>
          )}

          <Col style={{ textAlign: "right" }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              htmlType="submit"
              onClick={() => setLoading(true)}
              loading={loading}
            >
              Ekle
            </Button>
          </Col>
        </Col>
      </Row>
    </Form>
  );
};
export default PosCollectionBankInfoAddForm;
